import React, { useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  MenuItem,
  Select,
  Typography,
} from '@mui/material';
import VideocamIcon from '@mui/icons-material/Videocam';
import PhoneIcon from '@mui/icons-material/Phone';
import GavelIcon from '@mui/icons-material/Gavel';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import CourtEventApi, {
  APPEARANCE_LABELS,
  AppearanceRequirement,
} from '../../api/CourtEventApi';

/**
 * Does the client have to attend? (Hannah, Aug 8 2026 → Lloyd, Aug 10.)
 *
 * Hannah answers this question for third-party companies several times a day and currently checks
 * with Kelsi every time. Two things follow from that, and they are the whole design:
 *
 *   · **The provenance is shown, not just the answer.** A setting nobody has reviewed reads
 *     "unconfirmed"; one somebody has reads "confirmed by Kelsi". Lloyd wanted a plain default of
 *     "does not need to appear", which is right — but a default repeated to a third party as though
 *     it were checked is the mistake Hannah was trying to avoid.
 *   · **Choosing "must appear" emails the client, so it asks first.** Any staff member can set this,
 *     and the email goes immediately and cannot be recalled. A mis-click otherwise tells someone to
 *     take a day off work and attend court.
 */

interface AppearanceControlProps {
  courtEventGuid: string;
  value: AppearanceRequirement;
  setBy?: string | null;
  setAt?: string | null;
  /** False for reminders and deadlines — the question does not apply and the control hides. */
  isHearing?: boolean;
  /** Epoch ms. A setting in the past never notifies, and the dialog says so. */
  startEpoch: number;
  onChanged: (next: AppearanceRequirement) => void;
  disabled?: boolean;
}

const ICONS: Record<AppearanceRequirement, React.ReactElement> = {
  NOT_REQUIRED: <CheckCircleOutlineIcon fontSize="small" />,
  IN_PERSON: <GavelIcon fontSize="small" />,
  BY_VIDEO: <VideocamIcon fontSize="small" />,
  BY_PHONE: <PhoneIcon fontSize="small" />,
};

const AppearanceControl: React.FC<AppearanceControlProps> = ({
  courtEventGuid,
  value,
  setBy,
  setAt,
  isHearing = true,
  startEpoch,
  onChanged,
  disabled = false,
}) => {
  const [pending, setPending] = useState<AppearanceRequirement | null>(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  if (!isHearing) {
    return (
      <Typography variant="caption" color="text.secondary">
        Not a hearing — no appearance needed
      </Typography>
    );
  }

  const confirmed = Boolean(setAt);
  const isPast = startEpoch < Date.now();
  const willEmail = pending !== null && pending !== 'NOT_REQUIRED' && !isPast;

  const apply = async (next: AppearanceRequirement) => {
    setSaving(true);
    setError(null);
    setNotice(null);
    try {
      const updated = await CourtEventApi.updateCourtEvent({
        CourtEvent_guid: courtEventGuid,
        CourtEvent_AppearanceRequirement: next,
      });
      onChanged(next);
      // Say what actually happened. "Saved" alone would let staff believe the client was told when
      // they have no email on file, or when the send failed.
      const result = updated.appearanceNotification;
      if (result?.sent) {
        setNotice('Saved. The client has been emailed.');
      } else if (result?.skippedReason) {
        setNotice(`Saved, but the client was not emailed — ${result.skippedReason}.`);
      } else {
        setNotice('Saved.');
      }
      setPending(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not save that.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box>
      <FormControl size="small" fullWidth disabled={disabled || saving}>
        <Select
          value={value}
          onChange={(e) => {
            const next = e.target.value as AppearanceRequirement;
            if (next === value) return;
            // Everything routes through the dialog, including going back to "does not need to
            // appear" — staff should see that no correction is sent rather than assume one is.
            setPending(next);
          }}
          renderValue={(selected) => (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              {ICONS[selected as AppearanceRequirement]}
              {APPEARANCE_LABELS[selected as AppearanceRequirement]}
            </Box>
          )}
        >
          {(Object.keys(APPEARANCE_LABELS) as AppearanceRequirement[]).map((key) => (
            <MenuItem key={key} value={key}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                {ICONS[key]}
                {APPEARANCE_LABELS[key]}
              </Box>
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      <Box sx={{ mt: 0.5 }}>
        {confirmed ? (
          <Chip
            size="small"
            color="success"
            variant="outlined"
            label={`Confirmed by ${setBy || 'staff'}${
              setAt ? ` on ${new Date(setAt).toLocaleDateString('en-US')}` : ''
            }`}
          />
        ) : (
          <Chip size="small" color="warning" variant="outlined" label="Unconfirmed — nobody has checked this" />
        )}
      </Box>

      {notice && (
        <Alert severity="info" sx={{ mt: 1 }} onClose={() => setNotice(null)}>
          {notice}
        </Alert>
      )}
      {error && (
        <Alert severity="error" sx={{ mt: 1 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Dialog open={pending !== null} onClose={() => !saving && setPending(null)} maxWidth="sm" fullWidth>
        <DialogTitle>
          {willEmail ? 'This will email the client' : 'Change the appearance answer'}
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2 }}>
            You are recording: <strong>{pending ? APPEARANCE_LABELS[pending] : ''}</strong>
          </Typography>

          {willEmail && (
            <Alert severity="warning" sx={{ mb: 2 }}>
              The client will be emailed straight away telling them the court requires their
              attendance
              {pending === 'BY_VIDEO' ? ', by video' : pending === 'BY_PHONE' ? ', by phone' : ' in person'}, and texted if we have their
              mobile number. This cannot be undone — if it is wrong, you will need to call them.
            </Alert>
          )}

          {pending === 'NOT_REQUIRED' && (
            <Alert severity="info" sx={{ mb: 2 }}>
              No correction is sent. If the client was previously told to attend, call them.
            </Alert>
          )}

          {isPast && pending !== 'NOT_REQUIRED' && (
            <Alert severity="info" sx={{ mb: 2 }}>
              This court date has already passed, so nothing is sent to the client. The answer is
              recorded for the file.
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPending(null)} disabled={saving}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color={willEmail ? 'warning' : 'primary'}
            disabled={saving}
            onClick={() => pending && apply(pending)}
          >
            {saving ? 'Saving…' : willEmail ? 'Save and email the client' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AppearanceControl;
